import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import InputText from 'components/library/Input/InputText/InputText';
import InputDate from 'components/library/Input/InputDate/InputDate';
import LocationInput from 'components/library/Input/LocationInput';
import InputPhone from 'components/library/Input/InputPhone/InputPhone';
import InputRadioList from 'components/library/InputRadioList';
import isEmpty from 'utils/isEmpty';

import sharedStyles from '../Confirmation.module.css';
import styles from './ConfirmationPersonal.module.css';
import { AppDispatch } from 'store/types';
import * as statuses from 'store/states';

interface ErrorForm {
  gender?: string;
  phone?: string;
  firstname?: string;
  lastname?: string;
  birthday?: string;
  birthPlace?: string;
  address?: string;
  city?: string;
  zipCode?: string;
  country?: string;
}

interface Props {
  birthday: {
    day?: string;
    month?: string;
    year?: string;
  },
  address: string,
  zipCode: string,
  firstname: string,
  lastname: string,
  city: string,
  country: string,
  birthPlace: {
    address?: string;
    city?: string;
    zipCode?: string;
    country?: string;
  },
  phone: string,
  handleIsComplete: (bool: boolean) => void,
  checkValidity: boolean,
  submit: () => void,
  actions: {
    getInvestorInformation: () => void;
  },
  investorInformation: typeof statuses[keyof typeof statuses];
  societyId?: string | number,
  wantInvestBySociety: boolean,
  gender: string,
}

export default function ConfirmationPersonal({
  birthday, address, zipCode, firstname, lastname,
  city, country, birthPlace, phone, handleIsComplete, checkValidity, submit,
  actions, investorInformation, societyId, wantInvestBySociety, gender = 'Monsieur',
}: Props) {
  const MINIMUM_AGE_TO_INVEST = 13;

  const [errors, setErrors] = useState<ErrorForm>({});
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();

  useEffect(() => {
    if (investorInformation === 'IDLE') {
      actions.getInvestorInformation();
    }
  }, [actions, investorInformation, societyId, wantInvestBySociety]);

  const validate = () => {
    const newErrors: ErrorForm = {};
    if (errors.phone) newErrors.phone = t('phone.invalid');
    if (!firstname || firstname.trim() === '' || firstname.length > 64) {
      newErrors.firstname = t('coordonnate.firstnameInvalid');
    }
    if (!lastname || lastname.trim() === '' || lastname.length > 64) {
      newErrors.lastname = t('coordonnate.lastnameInvalid');
    }
    if (!birthday?.day || birthday?.day.trim() === '') {
      newErrors.birthday = t('coordonnate.birthdayDayInvalid');
    }
    if (!birthday?.month || birthday?.month.trim() === '') {
      newErrors.birthday = t('coordonnate.birthdayMonthInvalid');
    }
    if (!birthday?.year || birthday?.year.trim() === '') {
      newErrors.birthday = t('coordonnate.birthdayYearInvalid');
    } else if (birthday?.year && ((Number.parseInt(birthday?.year) < 1900 || (Number.parseInt(birthday?.year) > new Date().getFullYear())))) {
      newErrors.birthday = t('coordonnate.birthdayYear2Invalid');
    } else if (birthday?.year && (new Date().getFullYear() - Number.parseInt(birthday?.year) < MINIMUM_AGE_TO_INVEST)) {
      newErrors.birthday = t('coordonnate.birthdayTooYoung');
    }
    if (!birthPlace?.city || birthPlace?.city.trim() === '' || birthPlace?.city.length > 64) {
      newErrors.birthPlace = t('companyCoordonate.cityInvalid')
    }
    if (!birthPlace?.country || birthPlace?.country.trim() === '' || birthPlace?.country.length > 64) {
      newErrors.birthPlace = t('companyCoordonate.countryInvalid')
    }
    if (!address || address.trim() === '' || address.length > 256) {
      newErrors.address = t('companyCoordonate.addressInvalid')
    }
    if (!city || city.trim() === '' || city.length > 64) {
      newErrors.city = t('companyCoordonate.cityInvalid')
    }
    if (!zipCode || zipCode.trim() === '' || zipCode.length > 64) {
      newErrors.zipCode = t('companyCoordonate.zipCodeInvalid')
    }
    if (!country || country.trim() === '' || country.length > 64) {
      newErrors.country = t('companyCoordonate.countryInvalid')
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    } else {
      setErrors({});
      return true;
    }
  };

  const handlePhoneError = (bool: boolean) => {
    if (bool) {
      setErrors({ phone: t('phone.invalid') })
    } else {
      setErrors((data) => {
        const { phone, ...newData } = data;
        return newData;
      });
    }
  }

  useEffect(() => {
    const toCheck = [gender, birthday.day, birthday.month, birthday.year, address, zipCode, city, country, phone];
    if (checkValidity && validate()) {
      submit();
    } else if (checkValidity && !validate()) {
      handleIsComplete(false);
    }
    else if (!checkValidity) {
      handleIsComplete(toCheck.map((i) => !isEmpty(i)).reduce((c, a) => c && a, true));
    }
  }, [checkValidity, birthday, address, zipCode, gender, city, country, phone, birthPlace, handleIsComplete, submit])

  const setLocation = (name: string, value: string) => {
    if (name === 'address') {
      dispatch({ type: 'UPDATE_ADDRESS', payload: value });
    }
    else if (name === 'city') {
      dispatch({ type: 'UPDATE_CITY', payload: value });
    }
    else if (name === 'zipCode') {
      dispatch({ type: 'UPDATE_ZIP_CODE', payload: value });
    }
    else if (name === 'country') {
      dispatch({ type: 'UPDATE_COUNTRY', payload: value })
    }
  }

  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocation(e.target.name, e.target.value);
  }

  return (
    <div>
      <h2 className={sharedStyles.title}>
        {t('investments.personal')}
      </h2>
      <p className={sharedStyles.description}>
        {t('investments.personalDescription')}
      </p>
      <form
        className={styles.form}
        data-cy="confirmation-personal-form"
      >
        <div className={styles.half}>
          <InputRadioList
            title={t('account.title')}
            name="title"
            selected={gender}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => dispatch({ type: 'UPDATE_GENDER', payload: e.target.value })}
            buttons={[
              {
                label: t('account.mr'),
                value: 'Monsieur',
              },
              {
                label: t('account.ms'),
                value: 'Madame',
              },
              {
                label: t('account.other'),
                value: 'Autre',
              },
            ]}
            error={errors?.gender}
          />
        </div>
        <div className={styles.row}>
          <InputText
            label={t('account.firstname')}
            placeholder={t('account.firstname')}
            valueInput={firstname || ''}
            onTextChange={(e) => dispatch({ type: 'UPDATE_FIRSTNAME', payload: e.target.value })}
            state={(errors?.firstname) ? 'ERROR' : 'DEFAULT'}
            hint={errors?.firstname}
            maxLength={64}
            name="firstname"
          />
          <InputText
            label={t('account.lastname')}
            placeholder={t('account.lastname')}
            valueInput={lastname || ''}
            onTextChange={(e) => dispatch({ type: 'UPDATE_LASTNAME', payload: e.target.value })}
            state={(errors?.lastname) ? 'ERROR' : 'DEFAULT'}
            hint={errors?.lastname}
            maxLength={64}
            name="lastname"
          />
        </div>
        <InputDate
          label={t('coordonnate.birthday')}
          day={birthday.day || ''}
          month={birthday.month || ''}
          year={birthday.year || ''}
          onChange={(birthday) => dispatch({ type: 'UPDATE_BIRTHDAY', payload: birthday })}
          includeDay
          state={(errors?.birthday) ? 'ERROR' : 'DEFAULT'}
          hint={errors?.birthday}
          name="birthday"
        />
        <LocationInput
          label={t('coordonnate.birthplace')}
          values={{ ...birthPlace }}
          handleChange={(e: React.ChangeEvent<HTMLInputElement>) => dispatch({ type: 'UPDATE_BIRTHPLACE', payload: { name: e.target.name, value: e.target.value } })}
          setNewValues={(name, value) => dispatch({ type: 'UPDATE_BIRTHPLACE', payload: { name, value } })}
          showCountry
          showAddress={false}
          state={(errors?.birthPlace) ? 'ERROR' : 'DEFAULT'}
          hint={errors?.birthPlace}
          unrequired={['zipCode']}
          dataCy="birthplace"
        />
        <hr />
        <h2 className={sharedStyles.title}>
          {t('investments.contact')}
        </h2>
        <LocationInput
          label={t('coordonnate.residence')}
          values={{ address, zipCode, city, country }}
          handleChange={handleLocationChange}
          setNewValues={setLocation}
          showCountry
          state={(errors?.address) ? 'ERROR' : 'DEFAULT'}
          hint={errors?.address}
          dataCy="residence"
        />
        <InputPhone
          label={t('coordonnate.phone')}
          phone={phone}
          setPhone={(phone) => dispatch({ type: 'UPDATE_PHONE', payload: phone })}
          setError={handlePhoneError}
        />
      </form>
    </div>
  );
}
